import { defineMessages } from 'react-intl';

/**
 * Chip labels and their composer-seed prompts for
 * {@link WorkContextSuggestions}, one label/prompt pair per chip. The prompt is
 * user-visible too: selecting a chip seeds the composer textarea with it, so
 * both halves localize together.
 */
export const workContextPatientSuggestionMessages = defineMessages({
  summariseLabel: {
    defaultMessage: 'Summarise this patient',
    id: '+2kDQ7TgoJ',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  summarisePrompt: {
    defaultMessage: 'Summarise this patient’s history and current context.',
    id: 'jIIBagQs0j',
    description: 'Composer text seeded by the “Summarise this patient” chip',
  },
  updateContextLabel: {
    defaultMessage: 'Update patient context',
    id: 'r/0qibO2fv',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  updateContextPrompt: {
    defaultMessage: 'Update this patient’s context — ',
    id: 'aQsipxeJ+V',
    description:
      'Composer text seeded by the “Update patient context” chip; trails off so the user completes the sentence',
  },
  syncFromEhrLabel: {
    defaultMessage: 'Sync from my EHR',
    id: 'zvMR56hMAO',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  syncFromEhrPrompt: {
    defaultMessage: 'Sync this patient’s information from my EHR.',
    id: '4EQcDGfFrp',
    description: 'Composer text seeded by the “Sync from my EHR” chip',
  },
  exportToEhrLabel: {
    defaultMessage: 'Export to my EHR',
    id: 'gresYvYHRb',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  exportToEhrPrompt: {
    defaultMessage: 'Export this patient’s details to my EHR.',
    id: 'YX3m/lBpBh',
    description: 'Composer text seeded by the “Export to my EHR” chip',
  },
  exportPdfLabel: {
    defaultMessage: 'Export as PDF',
    id: 'VCTGjmp3f/',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  exportPdfPrompt: {
    defaultMessage: 'Export this patient’s summary as a PDF.',
    id: 'JVbld4VrBI',
    description: 'Composer text seeded by the patient “Export as PDF” chip',
  },
  emailLabel: {
    defaultMessage: 'Email',
    id: '+DefrHDmpB',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  emailPrompt: {
    defaultMessage: 'Draft an email about this patient.',
    id: 'O0DPOuy4Ex',
    description: 'Composer text seeded by the “Email” chip',
  },
  referralLetterLabel: {
    defaultMessage: 'Create referral letter',
    id: 'M5KXWtOTzS',
    description: 'Suggestion chip label on the linked-patient Work surface',
  },
  referralLetterPrompt: {
    defaultMessage: 'Create a referral letter for this patient.',
    id: 'LegfHEyK/c',
    description: 'Composer text seeded by the “Create referral letter” chip',
  },
});

export const workContextTemplateSuggestionMessages = defineMessages({
  updateTemplateLabel: {
    defaultMessage: 'Update this template',
    id: 'lADuh8tSKV',
    description: 'Suggestion chip label on the linked-template Work surface',
  },
  updateTemplatePrompt: {
    defaultMessage: 'Update this template — ',
    id: '0vUTuNctYG',
    description:
      'Composer text seeded by the “Update this template” chip; trails off so the user completes the sentence',
  },
  addSectionLabel: {
    defaultMessage: 'Add a section',
    id: 'g95/XtJEuc',
    description: 'Suggestion chip label on the linked-template Work surface',
  },
  addSectionPrompt: {
    defaultMessage: 'Add a new section to this template.',
    id: '9gEW6gco/2',
    description: 'Composer text seeded by the “Add a section” chip',
  },
  improveWordingLabel: {
    defaultMessage: 'Improve the wording',
    id: 'meLnCD6gCl',
    description: 'Suggestion chip label on the linked-template Work surface',
  },
  improveWordingPrompt: {
    defaultMessage: 'Improve the wording in this template.',
    id: 'c84WZn/RJb',
    description: 'Composer text seeded by the “Improve the wording” chip',
  },
  createNoteLabel: {
    defaultMessage: 'Create a note from this',
    id: 'ZS7fAjYjqG',
    description: 'Suggestion chip label on the linked-template Work surface',
  },
  createNotePrompt: {
    defaultMessage: 'Create a note using this template.',
    id: 'Nzj1s/mk1D',
    description: 'Composer text seeded by the “Create a note from this” chip',
  },
});

export const workContextFormSuggestionMessages = defineMessages({
  addFieldLabel: {
    defaultMessage: 'Add a field',
    id: 'bhK0zmyBaZ',
    description: 'Suggestion chip label on the linked-form Work surface',
  },
  addFieldPrompt: {
    defaultMessage: 'Add a new field to this form.',
    id: 'oExJ9shj6v',
    description: 'Composer text seeded by the “Add a field” chip',
  },
  updateFieldLabelsLabel: {
    defaultMessage: 'Update field labels',
    id: 'EHIok35wPP',
    description: 'Suggestion chip label on the linked-form Work surface',
  },
  updateFieldLabelsPrompt: {
    defaultMessage: 'Update the field labels on this form.',
    id: 'dThYMToJJ0',
    description: 'Composer text seeded by the “Update field labels” chip',
  },
  explainFieldsLabel: {
    defaultMessage: 'Explain the fields',
    id: 'Mwek6iZxpa',
    description: 'Suggestion chip label on the linked-form Work surface',
  },
  explainFieldsPrompt: {
    defaultMessage: 'Help me understand the fields in this form.',
    id: 'szjRCiY1rM',
    description: 'Composer text seeded by the “Explain the fields” chip',
  },
  exportPdfLabel: {
    defaultMessage: 'Export as PDF',
    id: 'ioU7Etl0+7',
    description: 'Suggestion chip label on the linked-form Work surface',
  },
  exportPdfPrompt: {
    defaultMessage: 'Export this form as a PDF.',
    id: 'Cz9P1RmUzU',
    description: 'Composer text seeded by the form “Export as PDF” chip',
  },
});

export const workContextSessionSuggestionMessages = defineMessages({
  updateNoteLabel: {
    defaultMessage: 'Update the note',
    id: 'O6mefZVSuv',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  updateNotePrompt: {
    defaultMessage: 'Update the consult note for this session — ',
    id: 'jehb6JiDdk',
    description:
      'Composer text seeded by the “Update the note” chip; trails off so the user completes the sentence',
  },
  createDocumentLabel: {
    defaultMessage: 'Create a document',
    id: 'w32iUBN/SF',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  createDocumentPrompt: {
    defaultMessage: 'Create a document from this session.',
    id: 'cyLIP+37kp',
    description: 'Composer text seeded by the “Create a document” chip',
  },
  summariseSessionLabel: {
    defaultMessage: 'Summarise this session',
    id: 'Byq5OzwL1b',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  summariseSessionPrompt: {
    defaultMessage: 'Summarise this session.',
    id: 'ad6kwCVbwv',
    description: 'Composer text seeded by the “Summarise this session” chip',
  },
  attachPatientLabel: {
    defaultMessage: 'Attach a patient',
    id: 'f0niE3WtSa',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  attachPatientPrompt: {
    defaultMessage: 'Attach a patient to this session.',
    id: '6PhUbertz5',
    description: 'Composer text seeded by the “Attach a patient” chip',
  },
  createPatientLabel: {
    defaultMessage: 'Create a patient from this',
    id: 'dQ9YnCKMS9',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  createPatientPrompt: {
    defaultMessage: 'Create a new patient profile from this session.',
    id: 'SOsNIfRNYu',
    description: 'Composer text seeded by the “Create a patient from this” chip',
  },
  exportPdfLabel: {
    defaultMessage: 'Export as PDF',
    id: '57A3IKdyzM',
    description: 'Suggestion chip label on the linked-session Work surface',
  },
  exportPdfPrompt: {
    defaultMessage: 'Export this session’s note as a PDF.',
    id: 'uAWQKWDBJO',
    description: 'Composer text seeded by the session “Export as PDF” chip',
  },
});
